import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS, ARTIST_STATUS_OVERRIDE } from "@/constants/catalog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { t } from "@/i18n";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const ReleaseChip = ({ r, role }) => {
  const st = (role === "artist" && ARTIST_STATUS_OVERRIDE[r.status]) || STATUS[r.status] || STATUS.draft;
  return (
    <Link to={`/lanzamientos/${r.id}`} data-testid={`calendar-release-${r.id}`} title={`${r.title} · ${r.artist_name} · ${t(st.label)}`}
      className={cn("block rounded border px-1.5 py-0.5 text-[11px] leading-tight truncate transition-colors hover:brightness-125", st.cls)}>
      {r.smartlink?.type === "presave" && <Link2 className="inline h-2.5 w-2.5 mr-1 -mt-0.5" />}{r.title}
    </Link>
  );
};

export default function CalendarPage() {
  const { user } = useAuth();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [releases, setReleases] = useState([]);
  useEffect(() => { api.get("/releases").then((r) => setReleases(r.data.filter((x) => x.live_date))).catch(() => {}); }, []);

  const byDay = useMemo(() => releases.reduce((m, r) => ((m[r.live_date] = [...(m[r.live_date] || []), r]), m), {}), [releases]);
  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1))];
  while (cells.length % 7) cells.push(null);
  const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;
  const inMonth = releases.filter((r) => r.live_date.startsWith(monthKey)).sort((a, b) => a.live_date.localeCompare(b.live_date));
  const upcoming = releases.filter((r) => r.live_date >= iso(today)).sort((a, b) => a.live_date.localeCompare(b.live_date)).slice(0, 8);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t("Planificación")}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Calendario de lanzamientos")}</h1>
          <p className="text-sm text-muted-foreground mt-2">{t("Fechas de estreno por mes. El icono de enlace indica que hay pre-save activo.")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="font-display font-semibold min-w-[170px] text-center" data-testid="calendar-month-label">{t(MONTHS[month])} {year}</span>
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} data-testid="calendar-today">{t("Hoy")}</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-6">
        <div className="panel overflow-hidden rise">
          <div className="grid grid-cols-7 border-b border-border bg-secondary/40">{DAYS.map((d) => <div key={d} className="px-2 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">{t(d)}</div>)}</div>
          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {cells.map((d, i) => {
              const key = d ? iso(d) : null;
              const isToday = key === iso(today);
              return (
                <div key={i} className={cn("min-h-[96px] border-b border-r border-border/60 p-1.5 space-y-1", !d && "bg-secondary/20", isToday && "bg-primary/5")} data-testid={key ? `calendar-day-${key}` : undefined}>
                  {d && <p className={cn("text-xs font-medium", isToday ? "text-primary" : "text-muted-foreground")}>{d.getDate()}</p>}
                  {key && (byDay[key] || []).slice(0, 3).map((r) => <ReleaseChip key={r.id} r={r} role={user.role} />)}
                  {key && (byDay[key] || []).length > 3 && <p className="text-[10px] text-muted-foreground">{t("+{n} más", { n: byDay[key].length - 3 })}</p>}
                </div>
              );
            })}
          </div>
        </div>
        <aside className="space-y-4">
          <div className="panel p-4 space-y-3 rise" data-testid="calendar-month-list">
            <p className="eyebrow flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5" /> {t("Este mes · {n}", { n: inMonth.length })}</p>
            {inMonth.length === 0 && <p className="text-sm text-muted-foreground">{t("Sin lanzamientos en {m}.", { m: t(MONTHS[month]) })}</p>}
            {inMonth.map((r) => (
              <Link key={r.id} to={`/lanzamientos/${r.id}`} className="flex items-center gap-3 text-sm hover:text-primary" data-testid={`calendar-list-${r.id}`}>
                <span className="font-mono text-xs text-muted-foreground w-8">{r.live_date.slice(8)}</span>
                <span className="truncate flex-1">{r.title}</span>
                <span className="text-xs text-muted-foreground truncate max-w-[90px]">{r.artist_name}</span>
              </Link>
            ))}
          </div>
          <div className="panel p-4 space-y-3 rise" data-testid="calendar-upcoming">
            <p className="eyebrow">{t("Próximos estrenos")}</p>
            {upcoming.length === 0 && <p className="text-sm text-muted-foreground">{t("Nada programado.")}</p>}
            {upcoming.map((r) => (
              <Link key={r.id} to={`/lanzamientos/${r.id}`} className="flex items-center gap-3 text-sm hover:text-primary">
                <span className="font-mono text-xs text-muted-foreground">{r.live_date}</span>
                <span className="truncate flex-1">{r.title}</span>
              </Link>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
